import axios from '../utils/axios';
import { RunProgress } from '../types/Run';

export async function createRun(data: any): Promise<any> {
  return axios.post('/runs', data).then((res) => res.data);
}

export async function updateRun(runId: string, data: any): Promise<any> {
  return axios.put(`/runs/${runId}`, data).then((res: any) => res.data);
}

/**
 * Partial update: only the keys present in `data` are written. Prefer this over
 * `updateRun` for ordinary metadata edits — a PUT re-writes every column of the
 * row, including the large `data_static` / `fit_file` / route-array blobs, which
 * also means it silently reverts anything a concurrent reprocess wrote.
 *
 * Not for reprocess (the server rejects `reprocess` here) and not for the
 * ingest trigger — see the comment on `RunEntry`'s file_name update.
 */
export async function patchRun(runId: string, data: any): Promise<any> {
  return axios.patch(`/runs/${runId}`, data).then((res: any) => res.data);
}

export async function deleteRun(runId: string): Promise<any> {
  return axios.delete(`/runs/${runId}`).then((res: any) => res.data);
}

export async function getRun(runId: string, queryString: string): Promise<any> {
  return axios.get(`/runs/${runId}?${queryString}`).then((res) => {
    return res.data;
  });
}

export async function getRuns(queryString: string): Promise<any> {
  return axios.get(`/runs?${queryString}`).then((res) => {
    return res.data;
  });
}

/**
 * Lightweight progress poll for an in-flight reprocess.
 * Hits the dedicated `GET /runs/<id>/progress` endpoint, which returns only the
 * small set of fields needed to drive a progress indicator (no data_static,
 * solve, etc.). Intended to be polled on a short interval while a reprocess is
 * running, then stopped once the run goes idle (progress columns cleared).
 */
export async function getRunProgress(runId: string): Promise<RunProgress> {
  return axios.get(`/runs/${runId}/progress`).then((res) => res.data);
}

/**
 * Batch progress poll: one request for many runs. Collapses the per-run
 * `/progress` fan-out (N requests per tick) into a single `POST /runs/progress`
 * so a slow backend receives fewer requests, not more. Unknown / not-owned ids
 * are simply absent from the response — callers map results back by id.
 */
export async function getRunsProgress(runIds: string[]): Promise<RunProgress[]> {
  if (runIds.length === 0) return [];
  return axios.post('/runs/progress', { ids: runIds }).then((res) => res.data);
}

export async function reprocessRuns(data: any): Promise<any> {
  // Whether a run rebuilds its zip from the episode blob chunks first is the
  // session's "Reprocess Source" setting, held per run in
  // rundata.reprocess_from_chunks — not something a reprocess request picks.
  const payload = data.runsToReprocess.map((run: any) => ({
    id: run.id,
    reprocess: true,
  }));

  return axios.put('/runs', payload).then((res: any) => res.data);
}

export async function updateRunsBulk(data: any[]): Promise<any> {
  return axios.put('/runs', data).then((res: any) => res.data);
}

/** Batch partial update — per-body narrow writes. See `patchRun`. */
export async function patchRunsBulk(data: any[]): Promise<any> {
  return axios.patch('/runs', data).then((res: any) => res.data);
}
